import { useEffect, useState } from "react";
import { Banknote, Landmark, User, X } from "lucide-react";
import { api } from "@/lib/api";

export const CONTACT_TYPES = [
  { v: "Customer", l: "Pelanggan" },
  { v: "Supplier", l: "Supplier" },
  { v: "Employee", l: "Karyawan" },
  { v: "Other", l: "Lainnya" },
];
export const typeLabel = (v) => CONTACT_TYPES.find((t) => t.v === v)?.l || v;
export const contactTypeLabels = (c) => (c.types?.length ? c.types : [c.type]).filter(Boolean).map(typeLabel).join(", ");

const EMPTY = {
  name: "", types: [], group: "", mobile: "", identity_type: "", identity_number: "", email: "", other_info: "",
  company: "", phone: "", fax: "", npwp: "", billing_address: "", shipping_address: "",
  bank_name: "", bank_branch: "", bank_holder: "", bank_account: "",
  receivable_account: "1-10100", payable_account: "2-20100", payment_terms: "30 hari",
};

export function ContactModal({ initial, defaultTypes, defaultName, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial, types: [(initial.types?.[0] || initial.type || "Customer")] } : { ...EMPTY, name: defaultName || "", types: [(defaultTypes?.[0] || "Customer")] });
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api("/accounts").then(setAccounts).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (v) => setForm((f) => ({ ...f, types: [v] }));
  const receivables = accounts.filter((a) => ["Accounts Receivable (A/R)", "Other Current Assets", "Cash & Bank"].includes(a.category) || a.code === form.receivable_account);
  const payables = accounts.filter((a) => ["Accounts Payable (A/P)", "Other Current Liabilities", "Long Term Liabilities"].includes(a.category) || a.code === form.payable_account);
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...form, type: form.types[0] || "Customer" };
      const r = await api(initial?.id ? `/contacts/${initial.id}` : "/contacts", { method: initial?.id ? "PATCH" : "POST", body: JSON.stringify(payload) });
      onSaved(r);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="contact-modal" onClick={onClose}>
      <div className="modal-panel contact-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="section-head">
          <div><p className="eyebrow">KONTAK</p><h2>{initial?.id ? `Ubah Kontak ${initial.name}` : "Buat Kontak Baru"}</h2></div>
          <button data-testid="contact-modal-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        {error && <div className="journal-balance bad" data-testid="contact-modal-error">{error}</div>}
        <div className="contact-modal-body">
          <div className="cm-section">
            <h3><User size={14} /> Informasi Umum</h3>
            <div className="cm-grid">
              <label>Nama Panggilan *<input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nama yang tampil di transaksi" /></label>
              <div className="cm-field">
                <span>Tipe Kontak (pilih satu)</span>
                <div className="cm-checks">
                  {CONTACT_TYPES.map((t) => (
                    <label key={t.v} className="cm-check"><input data-testid={`contact-type-${t.v.toLowerCase()}`} type="radio" name="contact-type" checked={form.types[0] === t.v} onChange={() => toggleType(t.v)} /> {t.l}</label>
                  ))}
                </div>
              </div>
              <label>Grup Kontak<input data-testid="contact-group-input" value={form.group} onChange={(e) => set("group", e.target.value)} placeholder="Opsional" /></label>
              <label>Handphone<input data-testid="contact-mobile-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></label>
              <div className="cm-field">
                <span>Identitas</span>
                <div className="cm-identity">
                  <select data-testid="contact-identity-type" value={form.identity_type} onChange={(e) => set("identity_type", e.target.value)}>
                    <option value="">Pilih</option><option value="KTP">KTP</option><option value="SIM">SIM</option><option value="Passport">Passport</option>
                  </select>
                  <input data-testid="contact-identity-number" value={form.identity_number} onChange={(e) => set("identity_number", e.target.value)} placeholder="Nomor ID" />
                </div>
              </div>
              <label>Email<input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></label>
              <label>Nama Perusahaan<input data-testid="contact-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} /></label>
              <label>Telepon<input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></label>
              <label>Fax<input data-testid="contact-fax-input" value={form.fax} onChange={(e) => set("fax", e.target.value)} /></label>
              <label>NPWP<input data-testid="contact-npwp-input" value={form.npwp} onChange={(e) => set("npwp", e.target.value)} /></label>
              <label>Info Lain<input data-testid="contact-other-input" value={form.other_info} onChange={(e) => set("other_info", e.target.value)} /></label>
              <label>Alamat Pembayaran<input data-testid="contact-billing-input" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></label>
              <label>Alamat Pengiriman<input data-testid="contact-shipping-input" value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} /></label>
            </div>
          </div>
          <div className="cm-section">
            <h3><Banknote size={14} /> Akun Bank</h3>
            <div className="cm-grid">
              <label>Nama Bank<input data-testid="contact-bank-name" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="mis. BCA" /></label>
              <label>Kantor Cabang Bank<input data-testid="contact-bank-branch" value={form.bank_branch} onChange={(e) => set("bank_branch", e.target.value)} /></label>
              <label>Pemegang Akun Bank<input data-testid="contact-bank-holder" value={form.bank_holder} onChange={(e) => set("bank_holder", e.target.value)} /></label>
              <label>Nomor Rekening<input data-testid="contact-bank-account" value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></label>
            </div>
          </div>
          <div className="cm-section">
            <h3><Landmark size={14} /> Pemetaan Akun</h3>
            <div className="cm-grid">
              <label>Akun Piutang
                <select data-testid="contact-receivable-select" value={form.receivable_account} onChange={(e) => set("receivable_account", e.target.value)}>
                  {receivables.map((a) => <option key={a.code} value={a.code}>{`(${a.code}) - ${a.name}`}</option>)}
                </select>
              </label>
              <label>Akun Hutang
                <select data-testid="contact-payable-select" value={form.payable_account} onChange={(e) => set("payable_account", e.target.value)}>
                  {payables.map((a) => <option key={a.code} value={a.code}>{`(${a.code}) - ${a.name}`}</option>)}
                </select>
              </label>
              <label>Syarat Pembayaran Utama
                <select data-testid="contact-terms-select" value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)}>
                  {["Tunai", "7 hari", "14 hari", "30 hari", "45 hari", "60 hari"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>
            <div className="cm-note" data-testid="contact-mapping-note">
              <b>Ketentuan pemetaan akun</b><br />
              Detail pemetaan akun tidak dapat diubah jika kontak sudah memiliki transaksi. Pastikan data sudah benar sebelum membuat kontak.
            </div>
          </div>
        </div>
        <div className="form-actions cm-actions">
          <button data-testid="contact-cancel-button" className="secondary-btn" onClick={onClose}>Tutup</button>
          <button data-testid="contact-save-button" className="primary-btn" disabled={!form.name.trim() || form.types.length === 0 || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}
